/* eslint-disable no-nested-ternary */
import clsx from 'clsx'
import SharePlus from 'shared-svg/SharePlus.svg'
import CommentIcon from 'shared-svg/CommentHollow.svg'
import PostedByFull from 'shared-components/ui/PostedByFull'
import { BasicStyledComponent, DSAuthored, DSPostMeta } from 'shared-definitions/types'
import styles from './PostShortLine.module.css'
import CommentsCount from '../posts/CommentsCount'
import ShareBar from './ShareBar'
import { commentsLabel } from '../comments/comment-format'
import { CommonDataContext } from '../contexts/CommonDataContext'
import Linked from '../service/Linked'
import { ConversationContext } from '../comments/conversation/ConversationContext'
import { useConversationPreview } from '../comments/conversation/useConversationPreview'

interface PostProps {
  pagePath: string | null
  title: string
  subtitle: string | null
  meta: DSPostMeta
  authors: DSAuthored | null
  firstPublishedDate: string | null
  date: string
}

interface PostShortLineProps extends BasicStyledComponent {
  post: PostProps
}

const PostShortLine: React.VFC<PostShortLineProps> = ({ className, post }) => {
  const {
    common: { shareOnGoogleLink },
  } = CommonDataContext.useContainer()
  // The page's conversation host, when it has one: the count opens the panel (comments always
  // in reach). Optional — a page without the provider or the host keeps the scroll.
  const conversation = ConversationContext.useOptionalContainer()
  // The count, from the preview the rail card / bar / end block share (no extra request; it
  // refreshes after the reader posts). Never a "0": until the preview says how many, the
  // batched counter draws a number only once it knows one above zero (`hideZero`); at zero the
  // button invites the first comment instead (empty state, 2026-10-24).
  const hostId =
    conversation?.containerId && conversation.containerId === post.meta.engagementContainerId
      ? conversation.containerId
      : null
  const preview = useConversationPreview(hostId)
  const knownTotal = hostId && preview.status === 'ready' ? preview.total : null
  const firstComment = knownTotal === 0
  // The button's name — its content can be an icon alone (count not known yet).
  let countLabel = 'Comments'
  if (firstComment) {
    countLabel = 'Be the first to comment'
  } else if (knownTotal) {
    countLabel = commentsLabel(knownTotal)
  }

  // "Jul 22, 2025 — 7:22 AM ET": the time is hidden below `sm` so the byline
  // fits one line on a phone; it stays in the DOM (same text for crawlers).
  const [dateDay, dateTime] = post.date.split(' — ')

  return (
    <div className={clsx(styles.container, className)}>
      <div className={styles.postedByContainer}>
        {post.meta.sponsor ? (
          <>
            <div className={styles.postedBy}>{post.meta.sponsor}</div>
            <div className={styles.dateDot}>{'•'}</div>
          </>
        ) : post.authors ? (
          <>
            <PostedByFull
              prefix="By"
              authors={post.authors.authoredBy}
              buttonClassName={styles.postedBy}
            />
            {post.authors.updatedBy.length > 0 && (
              <>
                <div className={styles.dateDot}>{'•'}</div>
                <PostedByFull
                  prefix="Updated by:"
                  authors={post.authors.updatedBy}
                  buttonClassName={styles.postedBy}
                />
              </>
            )}
            {post.authors.reviewedBy.length > 0 && (
              <>
                <div className={styles.dateDot}>{'•'}</div>
                <PostedByFull
                  prefix="Reviewed by:"
                  authors={post.authors.reviewedBy}
                  buttonClassName={styles.postedBy}
                />
              </>
            )}
            <div className={styles.dateDot}>{'•'}</div>
          </>
        ) : null}
        <p className={styles.date}>
          {/**
           * this "updated on" text is a hack, which will possible allow Google
           * to pick right published date
           * DT-7341
           * Rolled back as a quick fix
           */}
          {dateDay}
          {dateTime ? <span className={styles.dateTime}>{` — ${dateTime}`}</span> : null}
        </p>
        {post.firstPublishedDate ? (
          <>
            <div className={styles.dateDot}>{'•'}</div>
            <div
              className={styles.date}
            >{`Originally published on ${post.firstPublishedDate}`}</div>
          </>
        ) : null}
        {post.meta.engagementContainerId && (
          <>
            <div className={styles.dateDot}>{'•'}</div>
            <button
              className={clsx(styles.commentsCounterButton, {
                [styles.firstComment]: firstComment,
              })}
              type="button"
              aria-label={countLabel}
              onClick={() => {
                if (conversation?.containerId) {
                  conversation.open('byline', firstComment ? { focusComposer: true } : undefined)
                  return
                }
                const commentsSection = document.getElementById('comments-section')
                if (commentsSection) {
                  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
                  commentsSection.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth' })
                }
              }}
            >
              {firstComment ? (
                <>
                  <CommentIcon className={styles.firstCommentIcon} aria-hidden="true" />
                  {'Be the first to comment'}
                </>
              ) : (
                <CommentsCount
                  className={styles.commentsCounter}
                  containerId={post.meta.engagementContainerId}
                  count={knownTotal ?? undefined}
                  hideZero
                />
              )}
            </button>
          </>
        )}
      </div>
      <div className={styles.dateDot}>{'•'}</div>
      <ShareBar />

      {shareOnGoogleLink ? (
        <>
          <div className={styles.dateDot}>{'•'}</div>
          <Linked className={clsx(styles.date, styles.sharePlus)} {...shareOnGoogleLink.pLink}>
            <SharePlus className={styles.sharePlusIcon} />
            {shareOnGoogleLink.label}
          </Linked>
        </>
      ) : null}
    </div>
  )
}

export default PostShortLine
